import { Lock } from 'lucide-react';

import type { Contact } from '@/types/contacts';
import {
  getSourceLabel,
  getChannelLabel,
  formatLeadDate,
} from '@/lib/attributionDisplay';
import { EMPTY, type ColVis } from '@/lib/contacts';
import { StatusDropdown } from './StatusDropdown';
import { PaginationFooter } from './PaginationFooter';

interface Pagination {
  visibleCount: number;
  canLoadMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

function MobileRow({
  contact,
  isFirst,
  onOpen,
}: {
  contact: Contact;
  isFirst: boolean;
  onOpen: () => void;
}) {
  // Locked (over-visible-cap) row — identity stripped server-side; no drawer,
  // no status control. See PLAN_FREE_TIER_REDESIGN.md §5.
  if (contact.locked) {
    return (
      <div
        className={`grid select-none grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 ${isFirst ? '' : 'border-t-2 border-dark/20'}`}
        aria-label="Locked contact — upgrade to see it"
      >
        <div className="min-w-0">
          <p className="inline-flex items-center gap-1.5 font-mono text-sm text-muted-foreground">
            <Lock className="h-3 w-3" aria-hidden="true" />
            •• ••• ••••
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {formatLeadDate(contact.created_at)} ·{' '}
            {getSourceLabel(contact.source, contact.medium)}
          </p>
        </div>
        <span className="shrink-0 text-xs font-medium text-muted-foreground">
          Locked
        </span>
      </div>
    );
  }

  return (
    <div
      onClick={onOpen}
      className={`grid cursor-pointer grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 active:bg-bg-tint ${isFirst ? '' : 'border-t-2 border-dark/20'}`}
    >
      <div className="min-w-0">
        <p className="truncate font-mono text-sm text-dark">{contact.phone}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {formatLeadDate(contact.created_at)} ·{' '}
          {getSourceLabel(contact.source, contact.medium)}
        </p>
      </div>
      <div className="shrink-0">
        <StatusDropdown contact={contact} compact />
      </div>
    </div>
  );
}

// Mobile-only header block at the top of the Drawer showing the columns hidden
// on mobile (From, Path, Campaign, optional Line + Client). Lets users scan all
// data without expanding every row. Time is intentionally excluded — the
// ExpandedDetail metadata strip already shows the full timestamp.
export function MobileHiddenColumns({
  contact,
  colVis,
}: {
  contact: Contact;
  colVis: ColVis;
}) {
  const { showClient, showYourNumber } = colVis;
  const rows: { label: string; value: string }[] = [
    { label: 'From', value: getSourceLabel(contact.source, contact.medium) },
    {
      label: 'Path',
      value: getChannelLabel(contact.channel, contact.endpoint),
    },
    { label: 'Campaign', value: contact.campaign ?? EMPTY },
  ];
  if (showYourNumber) {
    rows.push({ label: 'Line', value: contact.tracking_number ?? EMPTY });
  }
  if (showClient) {
    rows.push({ label: 'Client', value: contact.client_name ?? EMPTY });
  }
  return (
    <dl className="mb-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 border-b border-dark/10 pb-4 text-sm">
      {rows.map((r) => (
        <div key={r.label} className="contents">
          <dt className="text-xs font-bold uppercase tracking-wider text-dark/60">
            {r.label}
          </dt>
          <dd className="break-all text-dark">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

interface Props {
  rows: Contact[];
  onOpen: (id: string) => void;
  pagination: Pagination;
}

// Mobile compressed table card.
export function MobileContactList({ rows, onOpen, pagination }: Props) {
  return (
    <div className="overflow-hidden rounded-3xl border border-dark/5 bg-card shadow-card md:hidden">
      <div className="grid grid-cols-[1fr_auto] gap-3 border-b-2 border-dark bg-white px-4 py-3 text-sm font-bold uppercase tracking-widest text-dark">
        <span>Mobile</span>
        <span>Status</span>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-12 text-center text-sm text-muted-foreground">
          No contacts match this filter.
        </p>
      ) : (
        rows.map((row, i) => (
          <MobileRow
            key={row.id}
            contact={row}
            isFirst={i === 0}
            onOpen={() => onOpen(row.id)}
          />
        ))
      )}
      <PaginationFooter {...pagination} />
    </div>
  );
}
